import type { LiveTimeBlock } from '@wiztime/core';
import { OverlapResolution } from '@wiztime/storage';
import { useTranslation } from 'react-i18next';
import { formatDuration, formatRange } from '../../../lib/format';
import { Button } from '../../../ui/Button';
import { useDayData } from '../DayDataContext';
import styles from './ProjectPicker.module.css';

interface ConflictResolverProps {
  /** The block the popover is about: "this block" of every action (ui-redesign §3.8). */
  readonly id: string;
  /** 「重叠部分改计入本块」: counts every overlapping minute towards this block (§2.1.5). */
  readonly onRecount: () => void;
  /** Trim or keep-one, applied to the pair of this block and the row's block (§2.1.8). */
  readonly onResolve: (otherId: string, mode: OverlapResolution) => void;
}

/** The greater `(countRank, id)` wins the overlapping minutes (data dictionary §3). */
function outranks(a: LiveTimeBlock, b: LiveTimeBlock): boolean {
  const rankA = a.data.countRank ?? 0;
  const rankB = b.data.countRank ?? 0;
  if (rankA !== rankB) return rankA > rankB;
  return a.id > b.id;
}

/**
 * Content of the overlap popover (ui-redesign §3.8): one row per confirmed block overlapping
 * this one, naming the overlapping stretch and the block its minutes count towards, with the
 * actions re-attribute, trim, and keep-one. An optional tidying tool: not opening it changes
 * nothing about recording or statistics.
 */
export function ConflictResolver({ id, onRecount, onResolve }: ConflictResolverProps) {
  const { t } = useTranslation();
  const { blocks, projects, conflicts } = useDayData();
  const subject = blocks.find((candidate) => candidate.id === id);
  if (subject === undefined) return null;
  const projectName = (block: LiveTimeBlock): string =>
    projects.byId.get(block.data.projectId)?.data.name ?? t('timeline.unknownProject');
  const others = conflicts.pairs
    .filter(([a, b]) => a === id || b === id)
    .map(([a, b]) => (a === id ? b : a))
    .map((otherId) => blocks.find((candidate) => candidate.id === otherId))
    .filter((block): block is LiveTimeBlock => block !== undefined)
    .sort((a, b) => a.data.startMin - b.data.startMin || (a.id < b.id ? -1 : 1));
  const subjectName = projectName(subject);
  // Two blocks of one project cannot be told apart by name: add the time range to the buttons.
  const keepLabel = (block: LiveTimeBlock, name: string, ambiguous: boolean): string =>
    t(ambiguous ? 'overlap.keepWithRange' : 'overlap.keep', {
      project: name,
      range: formatRange(block.data.startMin, block.data.endMin),
    });

  return (
    <div className={`${styles.picker} ${styles.resolver}`} data-testid="overlap-popover">
      <h2 className={styles.title}>{t('overlap.title', { project: subjectName })}</h2>
      <p className={styles.hint}>{t('overlap.hint')}</p>
      {others.map((other) => {
        const startMin = Math.max(subject.data.startMin, other.data.startMin);
        const endMin = Math.min(subject.data.endMin, other.data.endMin);
        const counted = outranks(subject, other) ? subject : other;
        const ambiguous = projectName(other) === subjectName;
        return (
          <div key={other.id} className={styles.overlapRow} data-testid="overlap-row">
            <p className={styles.hint}>
              {t('overlap.blockLine', {
                project: projectName(other),
                range: formatRange(other.data.startMin, other.data.endMin),
              })}
            </p>
            <p className={styles.hint} data-testid="overlap-segment">
              {t('overlap.segment', {
                range: formatRange(startMin, endMin),
                duration: formatDuration(endMin - startMin),
                project: projectName(counted),
              })}
            </p>
            <div className={styles.conflictActions}>
              {counted.id === subject.id ? null : (
                <Button className={styles.conflictButton} onClick={onRecount}>
                  {t('overlap.recount', { project: subjectName })}
                </Button>
              )}
              <Button
                className={styles.conflictButton}
                onClick={() => onResolve(other.id, OverlapResolution.Trim)}
              >
                {t('overlap.trim')}
              </Button>
              <Button
                className={styles.conflictButton}
                onClick={() => onResolve(other.id, OverlapResolution.KeepA)}
              >
                {keepLabel(subject, subjectName, ambiguous)}
              </Button>
              <Button
                className={styles.conflictButton}
                onClick={() => onResolve(other.id, OverlapResolution.KeepB)}
              >
                {keepLabel(other, projectName(other), ambiguous)}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
